import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { array, number, bool, string } from "prop-types";
import get from "lodash/get";
import { SliderArrow } from "./SliderArrow";
import "./scroll-snap.m.css";

const smoothScroll = (node, topOrLeft, horizontal) => {
  node.scrollTo({
    [horizontal ? "left" : "top"]: topOrLeft,
    behavior: "smooth",
  });
};

const updateItemSelection = (index, languageDirection, scroller, noOfItems) => {
  const getScrollValue = Math.floor(scroller.current.scrollWidth * (index / noOfItems));
  const scrollValue = languageDirection === "rtl" ? getScrollValue * -1 : getScrollValue;
  smoothScroll(scroller.current, scrollValue, true);
};

const setIndicatorValue = (func, ms) => {
  let timeout;

  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      func();
    }, ms);
  };
};

export const ScrollSnap = ({ children, interval, isInfinite, pauseOnHover, perView, storyElements }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [autoScroll, setAutoScroll] = useState(false);
  const languageDirection = useSelector((state) => get(state, ["qt", "config", "language", "direction"], "ltr"));
  const noOfItems = children ? children.length : 0;
  const scroller = useRef(null);
  const [imageDetails, setImageDetails] = useState({});
  const { title, imageAttribution } = imageDetails;
  const totalImages = storyElements.length;

  useEffect(() => {
    storyElements.map((image, index) => {
      index === selectedIndex && setImageDetails({ title: image.title, imageAttribution: image["image-attribution"] });
    });
  }, [selectedIndex]);

  useEffect(() => {
    if (scroller.current) {
      scroller.current.addEventListener(
        "scroll",
        setIndicatorValue(() => {
          const value = Math.round((scroller.current.scrollLeft / scroller.current.scrollWidth) * noOfItems);
          setSelectedIndex(Math.abs(value));
        }, 150)
      );

      // For Default Selection if selectedIndex is not 0 then manually select it
      if (selectedIndex !== 0) {
        updateItemSelection(selectedIndex);
      }
    }

    return () => {
      scroller.current.removeEventListener("scroll", setIndicatorValue);
    };
  }, []);

  // Infinite scroll
  useEffect(() => {
    if (isInfinite && !autoScroll) {
      const timer = setInterval(() => {
        infiniteScroll();
      }, interval);

      return () => {
        clearInterval(timer);
      };
    }
  }, [isInfinite, autoScroll]);

  const infiniteScroll = () => {
    setSelectedIndex((value) => {
      const newIndex = value === noOfItems - perView ? 0 : value + 1;
      updateItemSelection(newIndex, languageDirection, scroller, noOfItems);
      return newIndex;
    });
  };

  const pauseAutoPlay = () => {
    pauseOnHover && setAutoScroll(!autoScroll);
  };

  const nextClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (selectedIndex < noOfItems) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      updateItemSelection(newIndex, languageDirection, scroller, noOfItems);
    }
  };

  const previousClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newIndex = selectedIndex - 1;
    setSelectedIndex(newIndex);
    updateItemSelection(newIndex, languageDirection, scroller, noOfItems);
  };

  const showAttribution = (htmlNode) => {
    if (typeof window !== "undefined") {
      const tempDiv = window.document.createElement("div");
      tempDiv.innerHTML = htmlNode;
      const showAttribution = tempDiv.textContent;
      return showAttribution ? htmlNode : "";
    }
  };

  return (
    <div styleName="wrapper" className="scroll-snap-wrapper" onMouseEnter={pauseAutoPlay} onMouseLeave={pauseAutoPlay}>
      <div ref={scroller} styleName="carousel" className="scroll-snap-carousel">
        {children}
      </div>
      <div styleName="second-row">
        <div styleName="details">
        {showAttribution(title) && <p styleName="title" dangerouslySetInnerHTML={{ __html: title }} />}
          {showAttribution(imageAttribution) && (
            <div styleName="attr" dangerouslySetInnerHTML={{ __html: imageAttribution }} />
          )}
        </div>
        <div styleName="slide-arrow">
          <div>
            <SliderArrow
              noOfItems={noOfItems}
              perView={perView}
              previousClick={previousClick}
              nextClick={nextClick}
              selectedIndex={selectedIndex}
            />
          </div>
          <p styleName="digital-progress">
            {selectedIndex + 1} / {totalImages}
          </p>
        </div>
      </div>
    </div>
  );
};

ScrollSnap.defaultProps = {
  isArrow: true,
  isInfinite: false,
  interval: 4000,
  pauseOnHover: true,
  perView: 1,
  slideIndicator: "dots",
};

ScrollSnap.propTypes = {
  elements: array.isRequired,
  isArrow: bool,
  isInfinite: bool,
  interval: number,
  pauseOnHover: bool,
  perView: number,
  slideIndicator: string,
};
